import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import type { GeoJSON } from 'geojson'

export interface SearchBboxVisualization {
  bbox: [number, number, number, number] // [minLng, minLat, maxLng, maxLat]
  bboxArea?: number
  bboxSize?: { width: number; height: number }
  bboxCenter?: [number, number] // [lng, lat]
  searchCenter?: [number, number] // [lng, lat]
  searchRadiusMiles?: number
}

export interface UseSearchBboxLayerProps {
  visualization?: SearchBboxVisualization | null
}

/**
 * Hook for displaying the search bbox (bounding box) on the map
 * Shows the area that will be searched before the Overpass API call completes
 */
export function useSearchBboxLayer({
  visualization
}: UseSearchBboxLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const previousBboxRef = useRef<string | null>(null)

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    // Check if bbox changed
    const bboxKey = visualization?.bbox ? JSON.stringify(visualization.bbox) : null
    const bboxChanged = previousBboxRef.current !== bboxKey

    if (!bboxChanged) {
      return
    }

    previousBboxRef.current = bboxKey

    // Remove existing bbox layers if they exist
    if (map.current.getLayer('search-bbox-outline')) {
      map.current.removeLayer('search-bbox-outline')
    }
    if (map.current.getLayer('search-bbox-fill')) {
      map.current.removeLayer('search-bbox-fill')
    }
    if (map.current.getSource('search-bbox')) {
      map.current.removeSource('search-bbox')
    }

    // If no visualization data, don't show bbox
    if (!visualization?.bbox) {
      return
    }

    try {
      const [minLng, minLat, maxLng, maxLat] = visualization.bbox

      // Create rectangle polygon from bbox
      const bboxPolygon: GeoJSON.Polygon = {
        type: 'Polygon',
        coordinates: [[
          [minLng, minLat], // Southwest
          [maxLng, minLat], // Southeast
          [maxLng, maxLat], // Northeast
          [minLng, maxLat], // Northwest
          [minLng, minLat]  // Close the polygon
        ]]
      }

      const geoJsonData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {
              bboxArea: visualization.bboxArea,
              bboxSize: visualization.bboxSize
            },
            geometry: bboxPolygon
          }
        ]
      }

      // Add source
      map.current.addSource('search-bbox', {
        type: 'geojson',
        data: geoJsonData
      })

      // Add fill layer (semi-transparent)
      map.current.addLayer({
        id: 'search-bbox-fill',
        type: 'fill',
        source: 'search-bbox',
        paint: {
          'fill-color': '#f59e0b', // Orange/amber color to distinguish from route buffer
          'fill-opacity': 0.1 // Very transparent
        }
      })

      // Add outline layer (more visible)
      map.current.addLayer({
        id: 'search-bbox-outline',
        type: 'line',
        source: 'search-bbox',
        paint: {
          'line-color': '#f59e0b',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [3, 3] // Dashed line to distinguish from route buffer
        }
      })

      // Ensure bbox is below routes and markers but above base map
      // It should also be below route-buffer if it exists
      const layersToMoveAbove = [
        'route-buffer-outline',
        'route-buffer',
        'route-lines',
        'dead-end-markers',
        'waypoint-markers',
        'poi-markers',
        'forest-roads'
      ]
      
      for (const layerId of layersToMoveAbove) {
        if (map.current.getLayer(layerId)) {
          try {
            map.current.moveLayer('search-bbox-outline', layerId)
            map.current.moveLayer('search-bbox-fill', 'search-bbox-outline')
            break
          } catch (e) {
            // Layer might not exist, continue
          }
        }
      }
    } catch (error) {
    }
  }, [map, isLoaded, styleLoaded, visualization])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map.current) {
        try {
          if (map.current.getLayer('search-bbox-outline')) {
            map.current.removeLayer('search-bbox-outline')
          }
          if (map.current.getLayer('search-bbox-fill')) {
            map.current.removeLayer('search-bbox-fill')
          }
          if (map.current.getSource('search-bbox')) {
            map.current.removeSource('search-bbox')
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    }
  }, [map])
}
